/**
 * CoCM billing-summary hooks.
 *
 * `useCoCMBillingSummaries` lists a patient's persisted monthly summary
 * Observations (prior months for the billing tab); `useCoCMBillingSummary`
 * fetches a single patient-month. Both resolve the Observations into view
 * models and refetch on `CoCMBillingSummary*` domain events for the patient.
 *
 * @agentic connections
 */

import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMedplum } from "@medplum/react";
import {
  findCoCMBillingSummary,
  getEarliestCoCMEpisodeStart,
  searchCoCMBillingSummariesByPatient,
} from "@benchmark/core/services";
import { cocmBillingSummaryToView } from "@benchmark/core/resolvers";
import {
  DomainEventNames,
  globalEventEmitter,
  type CoCMBillingSummaryCreatedEvent,
  type CoCMBillingSummaryUpdatedEvent,
} from "@benchmark/core/events";
import type { CoCMBillingSummaryView } from "@benchmark/core/types";

const subscribeToSummaryEvents = (
  onEvent: (payload: { patientId: string }) => void,
): (() => void) => {
  const unsubCreated =
    globalEventEmitter.subscribe<CoCMBillingSummaryCreatedEvent>(
      DomainEventNames.CoCMBillingSummaryCreated,
      onEvent,
    );
  const unsubUpdated =
    globalEventEmitter.subscribe<CoCMBillingSummaryUpdatedEvent>(
      DomainEventNames.CoCMBillingSummaryUpdated,
      onEvent,
    );
  return () => {
    unsubCreated();
    unsubUpdated();
  };
};

/** Return shape for {@link useCoCMBillingSummaries}. @agentic connections */
export type UseCoCMBillingSummariesResult = {
  summaries: CoCMBillingSummaryView[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

/**
 * List a patient's persisted monthly billing summaries (newest first).
 * @agentic connections
 */
export const useCoCMBillingSummaries = (
  patientId: string | undefined,
): UseCoCMBillingSummariesResult => {
  const medplum = useMedplum();
  const [summaries, setSummaries] = useState<CoCMBillingSummaryView[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!patientId) {
      return;
    }
    let cancelled = false;
    startTransition(() => {
      setLoading(true);
      setError(undefined);
    });

    searchCoCMBillingSummariesByPatient(medplum, patientId)
      .then((observations) => {
        if (cancelled) return;
        setSummaries(observations.map(cocmBillingSummaryToView));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, patientId, version]);

  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });

  useEffect(() => {
    if (!patientId) return undefined;
    return subscribeToSummaryEvents((payload) => {
      if (payload.patientId === patientId) refetchRef.current();
    });
  }, [patientId]);

  return {
    summaries: patientId ? summaries : [],
    loading,
    error,
    refetch,
  };
};

/** Return shape for {@link useCoCMBillingSummary}. @agentic connections */
export type UseCoCMBillingSummaryResult = {
  summary: CoCMBillingSummaryView | undefined;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

/**
 * Fetch the single billing summary for a patient + month, if one exists.
 * @agentic connections
 */
export const useCoCMBillingSummary = (
  patientId: string | undefined,
  billingMonth: string | undefined,
): UseCoCMBillingSummaryResult => {
  const medplum = useMedplum();
  const [summary, setSummary] = useState<CoCMBillingSummaryView | undefined>(
    undefined,
  );
  // Block consumers (BillingModal) until the first fetch settles — `false` on
  // mount let the stepped wizard render with an empty summary for one frame
  // before the effect flipped loading, and raced fast/cached fetches against
  // a deferred `startTransition(() => setLoading(true))`.
  const [loading, setLoading] = useState<boolean>(() =>
    Boolean(patientId && billingMonth),
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  // Key of the last patient-month we've settled a fetch for (success OR error).
  // A `version`-bump refetch (fired by the CoCMBillingSummary* domain events
  // below, e.g. after saving a summary) refreshes in the background WITHOUT
  // re-entering the blocking `loading` state; only an initial load or a change
  // of patient/month does. BillingModal gates mounting its SteppedForm wizard
  // on this `loading`, so a refetch that flipped it back to `true` would
  // unmount and remount the wizard, snapping the user back to step 1.
  const loadedKeyRef = useRef<string | undefined>(undefined);
  const summaryCacheRef = useRef<
    Map<string, CoCMBillingSummaryView | undefined>
  >(new Map());

  useEffect(() => {
    if (!patientId || !billingMonth) {
      return;
    }
    const queryKey = `${patientId}:${billingMonth}`;
    // Initial load for this patient-month if we haven't settled a fetch for it
    // yet — an errored first attempt still counts as settled (recorded below),
    // so a subsequent event-driven refetch refreshes silently instead of
    // remounting the wizard. Drive `loading` directly off this so returning to
    // an already-loaded key clears any spinner left over from an in-flight,
    // now-superseded fetch for a different key.
    const isInitialLoad = loadedKeyRef.current !== queryKey;
    let cancelled = false;
    // Synchronous loading gate — must run before a cached Medplum read can
    // settle; deferred updates (startTransition) race fast reads and either mount
    // BillingModal with an empty summary or stick the spinner.
    /* eslint-disable react-hooks/set-state-in-effect -- intentional sync gate */
    if (isInitialLoad) {
      setLoading(true);
      if (summaryCacheRef.current.has(queryKey)) {
        setSummary(summaryCacheRef.current.get(queryKey));
      } else {
        setSummary(undefined);
      }
    } else if (loadedKeyRef.current === queryKey) {
      // Returning to an already-settled patient-month while another month's
      // fetch is still in-flight — drop the spinner without treating this as a
      // fresh blocking load (see event-refetch regression tests).
      if (summaryCacheRef.current.has(queryKey)) {
        setSummary(summaryCacheRef.current.get(queryKey));
      }
      setLoading(false);
    }
    setError(undefined);
    /* eslint-enable react-hooks/set-state-in-effect */

    findCoCMBillingSummary(medplum, patientId, billingMonth)
      .then((observation) => {
        if (cancelled) return;
        const view = observation
          ? cocmBillingSummaryToView(observation)
          : undefined;
        summaryCacheRef.current.set(queryKey, view);
        setSummary(view);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (cancelled) return;
        loadedKeyRef.current = queryKey;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, patientId, billingMonth, version]);

  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });

  useEffect(() => {
    if (!patientId) return undefined;
    return subscribeToSummaryEvents((payload) => {
      if (payload.patientId === patientId) refetchRef.current();
    });
  }, [patientId]);

  return {
    summary: patientId && billingMonth ? summary : undefined,
    loading,
    error,
    refetch,
  };
};

/**
 * Cap on how far back the billing tab synthesizes "not yet closed" prior
 * months (see the plan's Performance Considerations): bounds the per-month
 * `computeCoCMEligibility` fan-out for long-tenured patients.
 * @agentic connections
 */
export const MAX_EPISODE_MONTHS_LOOKBACK = 24;

/**
 * Pure: enumerate `YYYY-MM` keys from `earliestStartIso`'s month (inclusive)
 * through the month immediately before `beforeMonthKey` (exclusive),
 * capped to the most recent `maxMonths` keys. Returns oldest-first.
 * @agentic connections
 */
export const enumerateMonthKeysBefore = (
  earliestStartIso: string,
  beforeMonthKey: string,
  maxMonths: number = MAX_EPISODE_MONTHS_LOOKBACK,
): string[] => {
  const startMs = Date.parse(earliestStartIso);
  const [beforeYear, beforeMonth] = beforeMonthKey.split("-").map(Number);
  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(beforeYear) ||
    !Number.isFinite(beforeMonth)
  ) {
    return [];
  }
  const start = new Date(startMs);
  const startIndex = start.getUTCFullYear() * 12 + start.getUTCMonth();
  const beforeIndex = beforeYear * 12 + (beforeMonth - 1);

  const keys: string[] = [];
  for (let idx = startIndex; idx < beforeIndex; idx++) {
    const year = Math.floor(idx / 12);
    const month = (idx % 12) + 1;
    keys.push(`${year}-${String(month).padStart(2, "0")}`);
  }
  return keys.length > maxMonths ? keys.slice(keys.length - maxMonths) : keys;
};

/** Return shape for {@link useCoCMEpisodeMonths}. @agentic connections */
export type UseCoCMEpisodeMonthsResult = {
  /** `YYYY-MM` keys, oldest first, excluding `beforeMonthKey` itself. */
  months: string[];
  loading: boolean;
};

/**
 * Full-history `YYYY-MM` candidate months for a patient's CoCM billing tab:
 * every calendar month from the patient's earliest CoCM episode start
 * through the month before `beforeMonthKey`, capped at a 24-month lookback.
 * Does not filter by activity — callers combine this with
 * `computeCoCMEligibility` per month to drop zero-minute months (parity with
 * the close-month CLI's "no placeholder months" rule). Falls back to an
 * empty list (persisted-only behavior) if the episode-history lookup fails,
 * rather than blocking the tab's already-persisted cards.
 * @agentic connections
 */
export const useCoCMEpisodeMonths = (
  patientId: string | undefined,
  beforeMonthKey: string,
): UseCoCMEpisodeMonthsResult => {
  const medplum = useMedplum();
  const [months, setMonths] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!patientId) {
      startTransition(() => setMonths([]));
      return;
    }
    let cancelled = false;
    startTransition(() => setLoading(true));

    getEarliestCoCMEpisodeStart(medplum, patientId)
      .then((earliestStart) => {
        if (cancelled) return;
        setMonths(
          earliestStart
            ? enumerateMonthKeysBefore(earliestStart, beforeMonthKey)
            : [],
        );
      })
      .catch(() => {
        if (!cancelled) setMonths([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, patientId, beforeMonthKey]);

  return { months, loading };
};
